import { useEffect, useState } from "react";
import { Breadcrumb, Button, Cascader, Form, Input, message, Select, Space, Upload } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { useQuery } from "../../common/utils";
import { fetchRegionCascadeList, fetchSaleActivityExpertInfo, submitSaleActivityExpert } from "./api";
import UnitField from "./UnitField";
import "./activity.scss";

const uploadFields = [
  { field: "titleCertificateFileList", label: "职称证书", required: true, maxCount: 3 },
  { field: "doctorQualificationFileList", label: "医师资格证书", required: true, maxCount: 3 },
  { field: "doctorPracticeFileList", label: "医师执业证书", required: true, maxCount: 3 },
  { field: "educationFileList", label: "学历学位证明", required: true, maxCount: 3 },
  { field: "otherHonorFileList", label: "其他荣誉/资质材料", required: false, maxCount: 10 },
];

const requiredUploadFields = uploadFields.filter((item) => item.required);

const uploadFieldKeys = uploadFields.map((item) => item.field);

const titleOptions = [
  "主任医师",
  "副主任医师",
  "主治医师",
  "住院医师",
  "教授",
  "副教授",
  "讲师",
  "主任护师",
  "副主任护师",
  "主管护师",
  "主任技师",
  "副主任技师",
  "主管技师",
  "研究员",
  "副研究员",
  "助理研究员",
  "正高级工程师",
  "高级工程师",
  "工程师",
  "编审",
  "副编审",
  "编辑",
  "其他",
];

const departmentOptions = [
  "心血管内科",
  "呼吸内科",
  "消化内科",
  "神经内科",
  "肿瘤科",
  "外科",
  "普通外科",
  "骨科",
  "妇产科",
  "儿科",
  "急诊科",
  "重症医学科",
  "麻醉科",
  "影像科",
  "检验科",
  "药学部",
  "护理部",
  "教研室",
  "外科教研室",
  "其他",
];

const educationOptions = ["博士", "硕士", "本科", "大专", "其他"];

const certificateFileTypes = ["image/jpeg", "image/png", "application/pdf"];
const certificateFileExtensions = [".jpg", ".jpeg", ".png", ".pdf"];
const maxCertificateFileSize = 5 * 1024 * 1024;

const chineseNamePattern = /^[\u4e00-\u9fa5]{2,20}$/;
const idCardPattern = /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/;
const mobilePattern = /^1[3-9]\d{9}$/;
const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const certificateNumberPattern = /^[A-Za-z0-9-]{15,20}$/;
const bankCardPattern = /^\d{16,19}$/;

const mobileRules = [
  { required: true, message: "请输入手机号" },
  { pattern: mobilePattern, message: "请输入正确的手机号" },
];

const normalizeRegionTree = (list?: any[]): any[] =>
  (list || []).map((item) => ({
    ...item,
    value: item.value || item.code || item.regionCode || item.label || item.name,
    label: item.label || item.name || item.regionName || item.value,
    children: item.children && item.children.length ? normalizeRegionTree(item.children) : undefined,
  }));

const getRegionLabelsByValue = (options: any[], values: string[] = []) => {
  const labels: string[] = [];
  let currentOptions = options;
  values.forEach((value) => {
    const item = currentOptions.find((option) => option.value === value);
    if (item) {
      labels.push(item.label);
      currentOptions = item.children || [];
    }
  });
  return labels;
};

const getRegionValuesByLabels = (options: any[], labels: string[] = []) => {
  const values: string[] = [];
  let currentOptions = options;
  labels.forEach((label) => {
    const item = currentOptions.find((option) => option.label === label || option.value === label);
    if (item) {
      values.push(item.value);
      currentOptions = item.children || [];
    }
  });
  return values;
};

const normalizeSingleValue = (value: any) =>
  Array.isArray(value) ? value[value.length - 1] : value;

const renderOptions = (options: string[]) =>
  options.map((item) => <Select.Option key={item} value={item}>{item}</Select.Option>);

const handleSingleTagChange = (form: any, field: string) => (value: string[]) => {
  form.setFieldsValue({ [field]: value && value.length > 0 ? [value[value.length - 1]] : [] });
};

const normalizeUploadFile = (file: any, index: number) => ({
  fileUrl: file.fullPathFile || file.fileUrl || file.url || file.shortPathFile,
  fileName: file.fileName || file.name || file.originalFilename,
  fileSize: file.fileSize || file.size,
  showOrder: index + 1,
});

const normalizeUploadFileList = (list?: any[]) =>
  (list || []).map((file, index) => {
    const normalized = normalizeUploadFile(file, index);
    return {
      ...file,
      ...normalized,
      uid: String(file.uid || file.id || `${normalized.fileUrl || normalized.fileName || "file"}-${index}`),
      name: normalized.fileName || `附件${index + 1}`,
      status: "done",
      url: normalized.fileUrl,
    };
  });

export default function SaleActivityExpertSignUp() {
  const query = useQuery();
  const history = useHistory();
  const activityId = query.get("activityId") || "";
  const [form] = Form.useForm();
  const [fileMap, setFileMap] = useState<any>({});
  const [regionOptions, setRegionOptions] = useState<any[]>([]);

  useEffect(() => {
    Promise.all([fetchRegionCascadeList(), fetchSaleActivityExpertInfo()]).then(([regionRes, expertRes]) => {
      const options = normalizeRegionTree(regionRes.data || []);
      const data = expertRes.data || {};
      const formValues: any = { ...data };
      if (!formValues.unitInstituteId && formValues.unit) {
        formValues.unitInstituteId = undefined;
      }
      if (formValues.province || formValues.city || formValues.district) {
        const region = getRegionValuesByLabels(
          options,
          [formValues.province, formValues.city, formValues.district].filter(Boolean)
        );
        formValues.region = region.length > 0
          ? region
          : [formValues.province, formValues.city, formValues.district].filter(Boolean);
      }
      if (formValues.department && !Array.isArray(formValues.department)) {
        formValues.department = [formValues.department];
      }
      if (formValues.title && !Array.isArray(formValues.title)) {
        formValues.title = [formValues.title];
      }
      uploadFieldKeys.forEach((field) => {
        delete formValues[field];
      });
      setRegionOptions(options);
      form.setFieldsValue(formValues);
      const nextFileMap: any = {};
      uploadFieldKeys.forEach((field) => {
        nextFileMap[field] = normalizeUploadFileList(data[field]);
      });
      setFileMap(nextFileMap);
    });
  }, [form]);

  const beforeUpload = (field: string) => (file: any) => {
    const uploadField = uploadFields.find((item) => item.field === field);
    const fileList = fileMap[field] || [];
    const fileName = String(file.name || "").toLowerCase();
    const isAllowedType =
      certificateFileTypes.includes(file.type) ||
      certificateFileExtensions.some((extension) => fileName.endsWith(extension));
    if (!isAllowedType) {
      message.error("仅支持 jpg、png、pdf 格式");
      return Upload.LIST_IGNORE;
    }
    if (file.size > maxCertificateFileSize) {
      message.error("单个附件不能超过 5M");
      return Upload.LIST_IGNORE;
    }
    if (uploadField && fileList.length >= uploadField.maxCount) {
      message.error(`${uploadField.label}最多上传 ${uploadField.maxCount} 个文件`);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const uploadRequest = (field: string) => async (data: any) => {
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("uploadDataType", "file");
    formData.append("folder", "saleActivity");
    try {
      const response: any = await axios.post("/shop/api/upload/file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.code === 0 || response.data.code === 200) {
        const item = normalizeUploadFile(
          { ...response.data.data, name: data.file.name, size: data.file.size },
          (fileMap[field] || []).length
        );
        const uploadItem = {
          ...item,
          uid: `${item.fileUrl || data.file.uid || data.file.name}-${Date.now()}`,
          name: item.fileName || data.file.name,
          status: "done",
          url: item.fileUrl,
        };
        setFileMap((prev: any) => ({ ...prev, [field]: [...(prev[field] || []), uploadItem] }));
        data.onSuccess(response.data);
        message.success("上传成功");
      } else {
        data.onError(response.data);
        message.error(response.data.msg || response.data.message || "上传失败");
      }
    } catch (error) {
      data.onError(error);
      message.error("上传失败");
    }
  };

  const submit = () => {
    form.validateFields().then((values) => {
      const missingUpload = requiredUploadFields.find((item) => !fileMap[item.field] || fileMap[item.field].length === 0);
      if (missingUpload) {
        message.info(`请上传${missingUpload.label}`);
        return;
      }
      const regionLabels = getRegionLabelsByValue(regionOptions, values.region || []);
      const params: any = {
        ...values,
        province: regionLabels[0] || values.province,
        city: regionLabels[1] || values.city,
        district: regionLabels[2] || values.district,
        unit: values.unit || form.getFieldValue("unit"),
        department: normalizeSingleValue(values.department),
        title: normalizeSingleValue(values.title),
        serviceAgreementConfirm: 1,
        activityId: Number(activityId),
        ...fileMap,
      };
      delete params.region;
      if (!params.unitInstituteId) {
        delete params.unitInstituteId;
      }
      if (params.age !== "" && params.age !== undefined && params.age !== null) {
        const age = Number(params.age);
        params.age = Number.isNaN(age) ? params.age : age;
      }
      submitSaleActivityExpert({
        ...params,
      }).then((res) => {
        if (res.code === 0 || res.code === 200 || res.code === 2000) {
          message.success(res.message || res.msg || "提交成功");
          history.push(`/saleActivityDetail?activityId=${activityId}`);
        }
      });
    });
  };

  return (
    <div className="sale-activity-expert-signup-page">
      <div className="sale-activity-user-signup-breadcrumb">
        <Breadcrumb separator=">">
          <Breadcrumb.Item>活动报名</Breadcrumb.Item>
          <Breadcrumb.Item>活动详情</Breadcrumb.Item>
          <Breadcrumb.Item>专家报名</Breadcrumb.Item>
        </Breadcrumb>
      </div>
      <div className="sale-activity-expert-signup-body">
        <div className="sale-activity-form sale-activity-signup-form">
          <div className="sale-activity-form__title">专家报名信息</div>
          <Form form={form} layout="vertical">
            <div className="sale-activity-form-block">
              <div className="sale-activity-form-block__title">基础信息</div>
              <div className="sale-activity-form-grid">
                <Form.Item
                  name="name"
                  label="姓名"
                  rules={[
                    { required: true, message: "请输入姓名" },
                    { pattern: chineseNamePattern, message: "请输入2-20位中文姓名" },
                  ]}
                >
                  <Input
                    placeholder="请输入姓名"
                    maxLength={20}
                    onBlur={() => {
                      const { name, accountName } = form.getFieldsValue();
                      if (name && !accountName) {
                        form.setFieldsValue({ accountName: name });
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item name="sex" label="性别" rules={[{ required: true, message: "请选择性别" }]}>
                  <Select allowClear placeholder="请选择性别">
                    <Select.Option value="男">男</Select.Option>
                    <Select.Option value="女">女</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="idCard"
                  label="身份证号"
                  rules={[
                    { required: true, message: "请输入身份证号" },
                    { pattern: idCardPattern, message: "请输入正确的18位身份证号" },
                  ]}
                >
                  <Input placeholder="请输入身份证号" maxLength={18} />
                </Form.Item>
                <Form.Item
                  name="mobile"
                  label="手机号"
                  rules={mobileRules}
                >
                  <Input placeholder="请输入手机号" maxLength={11} />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="电子邮箱"
                  rules={[{ pattern: emailPattern, message: "请输入正确的邮箱" }]}
                >
                  <Input placeholder="请输入电子邮箱" maxLength={100} />
                </Form.Item>
                <Form.Item
                  name="region"
                  label="所在省市区"
                  required
                  rules={[{
                    validator: (_, value) =>
                      value && value.length >= 2
                        ? Promise.resolve()
                        : Promise.reject(new Error("请选择所在省市")),
                  }]}
                >
                  <Cascader
                    allowClear
                    showSearch
                    options={regionOptions}
                    placeholder="请选择所在省市区"
                  />
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                  {() => (
                    <Form.Item
                      name="unitInstituteId"
                      label="所在单位"
                      required
                      rules={[{
                        validator: () => {
                          const values = form.getFieldsValue();
                          return values.unit
                            ? Promise.resolve()
                            : Promise.reject(new Error("请选择或输入所在单位"));
                        },
                      }]}
                    >
                      <UnitField form={form} mode="expert" />
                    </Form.Item>
                  )}
                </Form.Item>
                <Form.Item name="department" label="部门科室" rules={[{ required: true, message: "请选择或输入部门科室" }]}>
                  <Select allowClear showSearch mode="tags" maxTagCount={1} placeholder="请选择或输入部门科室" onChange={handleSingleTagChange(form, "department")}>
                    {renderOptions(departmentOptions)}
                  </Select>
                </Form.Item>
                <Form.Item name="post" label="行政职务" rules={[{ max: 50, message: "行政职务最多50字符" }]}>
                  <Input placeholder="请输入行政职务" maxLength={50} />
                </Form.Item>
                <Form.Item name="title" label="专业职称" rules={[{ required: true, message: "请选择或输入专业职称" }]}>
                  <Select allowClear showSearch mode="tags" maxTagCount={1} placeholder="请选择或输入专业职称" onChange={handleSingleTagChange(form, "title")}>
                    {renderOptions(titleOptions)}
                  </Select>
                </Form.Item>
              </div>
            </div>
            <div className="sale-activity-form-block">
              <div className="sale-activity-form-block__title">执业资质</div>
              <div className="sale-activity-form-grid">
                <Form.Item
                  name="doctorQualificationNumber"
                  label="医师资格证号"
                  rules={[
                    { required: true, message: "请输入医师资格证号" },
                    { pattern: certificateNumberPattern, message: "请输入15-20位证书编号" },
                  ]}
                >
                  <Input placeholder="请输入医师资格证号" maxLength={20} />
                </Form.Item>
                <Form.Item
                  name="doctorPracticeNumber"
                  label="医师执业证号"
                  rules={[
                    { required: true, message: "请输入医师执业证号" },
                    { pattern: certificateNumberPattern, message: "请输入15-20位证书编号" },
                  ]}
                >
                  <Input placeholder="请输入医师执业证号" maxLength={20} />
                </Form.Item>
                <Form.Item name="practiceRange" label="执业范围" rules={[{ required: true, message: "请输入执业范围" }, { max: 50, message: "执业范围最多50字符" }]}>
                  <Input placeholder="请输入执业范围" maxLength={50} />
                </Form.Item>
                <Form.Item name="highestEducation" label="最高学历" rules={[{ required: true, message: "请选择最高学历" }]}>
                  <Select allowClear placeholder="请选择最高学历">
                    {renderOptions(educationOptions)}
                  </Select>
                </Form.Item>
                <Form.Item name="graduateFrom" label="毕业院校" rules={[{ required: true, message: "请输入毕业院校" }, { max: 50, message: "毕业院校最多50字符" }]}>
                  <Input placeholder="请输入毕业院校" maxLength={50} />
                </Form.Item>
              </div>
            </div>
            <div className="sale-activity-form-block">
              <div className="sale-activity-form-block__title">专业能力</div>
              <div className="sale-activity-form-grid">
                <Form.Item className="sale-activity-form-item--full" name="researchDirection" label="研究方向" rules={[{ required: true, message: "请输入研究方向" }, { max: 200, message: "研究方向最多200字符" }]}><Input.TextArea rows={6} maxLength={200} placeholder="请输入研究方向" /></Form.Item>
                <Form.Item className="sale-activity-form-item--full" name="areaOfExpertise" label="擅长领域" rules={[{ max: 200, message: "擅长领域最多200字符" }]}><Input.TextArea rows={6} maxLength={200} placeholder="请输入擅长领域" /></Form.Item>
                <Form.Item className="sale-activity-form-item--full" name="academicAppointment" label="学术任职" rules={[{ max: 200, message: "学术任职最多200字符" }]}><Input.TextArea rows={6} maxLength={200} placeholder="请输入学术任职" /></Form.Item>
                <Form.Item className="sale-activity-form-item--full" name="personalProfile" label="个人简介" rules={[{ max: 500, message: "个人简介最多500字符" }]}><Input.TextArea rows={6} maxLength={500} placeholder="请输入个人简介" /></Form.Item>
                <Form.Item className="sale-activity-form-item--full" name="educationTrainingExperience" label="教培经验" rules={[{ required: true, message: "请输入教培经验" }, { max: 500, message: "教培经验最多500字符" }]}><Input.TextArea rows={6} maxLength={500} placeholder="请输入教培经验" /></Form.Item>
                <Form.Item className="sale-activity-form-item--full" name="relatedResearch" label="相关研究" rules={[{ max: 500, message: "相关研究最多500字符" }]}><Input.TextArea rows={6} maxLength={500} placeholder="请输入相关研究" /></Form.Item>
              </div>
            </div>
            <div className="sale-activity-form-block">
              <div className="sale-activity-form-block__title">证明材料</div>
              <div className="sale-activity-upload-grid">
                {uploadFields.map(({ field, label, required, maxCount }) => (
                  <Form.Item key={field} label={`${label}扫描件`} required={required}>
                    <Upload
                      accept=".jpg,.jpeg,.png,.pdf"
                      beforeUpload={beforeUpload(field)}
                      customRequest={uploadRequest(field)}
                      fileList={fileMap[field] || []}
                      multiple
                      maxCount={maxCount}
                      onRemove={(file) => {
                        setFileMap((prev: any) => ({
                          ...prev,
                          [field]: (prev[field] || []).filter((item: any) => item.uid !== file.uid),
                        }));
                      }}
                    >
                      <Button className="sale-activity-expert-upload-button" icon={<PlusOutlined />} />
                    </Upload>
                  </Form.Item>
                ))}
              </div>
            </div>
            <div className="sale-activity-form-block">
              <div className="sale-activity-form-block__title">劳务结算</div>
              <div className="sale-activity-form-grid">
                <Form.Item name="accountBank" label="开户银行" rules={[{ required: true, message: "请输入开户银行" }, { max: 50, message: "开户银行最多50字符" }]}><Input placeholder="请输入开户银行" maxLength={50} /></Form.Item>
                <Form.Item name="accountBranchBank" label="开户支行" rules={[{ required: true, message: "请输入开户支行" }, { max: 100, message: "开户支行最多100字符" }]}><Input placeholder="请输入开户支行" maxLength={100} /></Form.Item>
                <Form.Item name="bankCardNumber" label="银行卡号" rules={[{ required: true, message: "请输入银行卡号" }, { pattern: bankCardPattern, message: "请输入16-19位银行卡号" }]}><Input placeholder="请输入银行卡号" maxLength={19} /></Form.Item>
                <Form.Item
                  name="accountName"
                  label="开户人姓名"
                  rules={[
                    { required: true, message: "请输入开户人姓名" },
                    { validator: (_, value) => {
                      const name = form.getFieldValue("name");
                      return !value || !name || value === name
                        ? Promise.resolve()
                        : Promise.reject(new Error("开户人姓名需与姓名一致"));
                    } },
                  ]}
                >
                  <Input placeholder="请输入开户人姓名" maxLength={20} />
                </Form.Item>
              </div>
            </div>
            <div className="sale-activity-form-actions">
              <Space>
                <Button onClick={() => history.push(`/saleActivityDetail?activityId=${activityId}`)}>取消</Button>
                <Button type="primary" onClick={submit}>提交报名</Button>
              </Space>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}
